"use client";

import Image from "next/image";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import SmallButtonWrapper from "@/components/atoms/button-wrapper/small-button-wrapper";
import ImageAtom, { MagnoliaImage } from "@/components/atoms/image/image";
import withApplication from "@/components/with-application";
import {
  Quote,
  RenewalPage,
  OptimizelyExperimentsEnum,
} from "@/config/app-constant";
import {
  ANNUAL_MULTI_TRIP,
  BASIC,
  BRAND_STAYSURE,
  BRAND_AVANTI,
  PRODUCT,
} from "@/constants";
import { usePromoBanner } from "@/hooks/use-promo-banner";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { RootState } from "@/store/root-reducer";
import { updateisLoadingCoverState } from "@/store/slices/quote/quote.slice";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";
import { resolveMagnoliaAssetUrl } from "@/utils/cms-image-resolver";
import defaqtoStyles from "@/utils/defaqto-brand-colors.utils";
import headerClassNames from "@/utils/get-brand-quote-cover-top-classes.utils";
import isShowExclusiveBanner from "@/utils/online-exclusive-price-banner-show.utils";

import styles from "./cover-level-selection-card-top.module.css";
import AppliedPromo from "../applied-promo";
import Defaqto from "../defaqto/defaqto";
import OnlineExclusiveBanner from "../online-exclusive-price-banner/online-exclusive-banner";
import PriceToggle from "../price-toggle/price-toggle";

type HeaderClassNamesType = {
  headerClassName: string;
  headerDetaisClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
  };
  topSectionclassName: string;
};

type CoverSingleCardProps = {
  cardTopData?: {
    cardTitle?: string;
    selectBtnInactiveLable?: string;
    selectBtnActiveLable?: string;
    isLoading?: boolean;
  };
  cover: PolicyPackagePrice | undefined;
  defaqtoStarts: 1 | 2 | 3 | 4 | 5;
  whichBestBuyBadge?: MagnoliaImage | string;
  id?: number;
  isSelect?: boolean;
  keyValue: string;
  onlineExclusiveBannerLabel?: string;
  referenceId?: string;
  selectState?: boolean;
  type?: string;
  onSelect?: (val: string, state: boolean, index: number) => void;
  application?: string;
  onTripTypeToggle?: (tripType: string) => void;
};

function CoverSingleCardTop({
  cardTopData = {},
  cover,
  defaqtoStarts = 1,
  whichBestBuyBadge,
  id = 0,
  isSelect = false,
  keyValue = "",
  onlineExclusiveBannerLabel,
  referenceId,
  selectState = false,
  type = "",
  onSelect,
  application,
  onTripTypeToggle,
}: Readonly<Partial<CoverSingleCardProps>>) {
  const dispatch = useDispatch();
  const isQuoteDisabled = useQuoteDisabled();
  const { getPriceComparison, isPromoBannerActive } = usePromoBanner();
  const headerClassNamesList: HeaderClassNamesType =
    headerClassNames[keyValue as keyof typeof headerClassNames];
  const { headerClassName, headerDetaisClassNames, topSectionclassName } =
    headerClassNamesList;
  const offlinePriceSubTextColor =
    PRODUCT === BRAND_AVANTI
      ? headerDetaisClassNames?.subTextClassName
      : "text-dark-text-functional-subtle";

  const { current } = useWizard();
  const isExpandSelectButton =
    current.brandSpecificParams?.isExpandSelectButton;
  const isHideExclusiveBanner =
    current.brandSpecificParams?.isHideExclusiveBanner;
  const isHideTotalPriceWithoutDiscounts =
    current.brandSpecificParams?.isHideTotalPriceWithoutDiscounts;
  const isHideStrikethroughAndExclusivePrice =
    current.uiElementControls?.isHideStrikethroughAndExclusivePrice ||
    isPromoBannerActive;
  const isDisplayWhichBestBuyBadge =
    current.brandSpecificParams?.isDisplayWhichBestBuyBadge;
  const isBasicCoverButtonMarginEnabled =
    current.uiElementControls?.isBasicCoverButtonMarginEnabled;

  const isRenewalBadge =
    application === RenewalPage && typeof whichBestBuyBadge === "string";

  const renewalBadgeSrc = useMemo(
    () =>
      isRenewalBadge && whichBestBuyBadge
        ? resolveMagnoliaAssetUrl(whichBestBuyBadge)
        : "",
    [isRenewalBadge, whichBestBuyBadge]
  );

  const shouldShowWhichBadge = useMemo(() => {
    const isEligibleCoverLevel =
      keyValue === "COMPREHENSIVE" || keyValue === "SIGNATURE";

    // Renewal: badge is delivered as a raw DAM reference string (jcr:<uuid>)
    if (isRenewalBadge) {
      return Boolean(
        isDisplayWhichBestBuyBadge &&
          whichBestBuyBadge.trim().length > 0 &&
          isEligibleCoverLevel
      );
    }

    // Quote: badge is the expanded MagnoliaImage object
    const hasValidBadgeData =
      whichBestBuyBadge &&
      (whichBestBuyBadge as MagnoliaImage)["@id"] &&
      (whichBestBuyBadge as MagnoliaImage)["@path"];

    return Boolean(
      isDisplayWhichBestBuyBadge && hasValidBadgeData && isEligibleCoverLevel
    );
  }, [isDisplayWhichBestBuyBadge, whichBestBuyBadge, keyValue, isRenewalBadge]);

  const isPriceToggleEnabled = useMemo(() => {
    const isExperimentEnabled =
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.PRICE_TOGGLE_EXPERIMENT
      ) === "variant";
    const isDefinitionEnabled =
      current.brandSpecificParams?.priceToggleExperimentEnabled === true;

    return isExperimentEnabled && isDefinitionEnabled;
  }, [current.brandSpecificParams?.priceToggleExperimentEnabled]);

  const { attributes } = useSelector((state: RootState) => state.quote);
  const isInitialAMT = useMemo(() => {
    return (
      attributes.tripDetails?.toLocation === null ||
      attributes.tripDetails?.toLocation === undefined
    );
  }, [attributes.tripDetails?.toLocation]);

  const appliedPromoCode = useSelector(
    (state: RootState) =>
      state.quote.attributes?.paymentDetails?.discounts?.promoCode
  );

  // Helper: Check if valid promo code is applied
  const isPromoCodeApplied =
    isPromoBannerActive && appliedPromoCode?.valid && appliedPromoCode?.code;

  // Helper: Check if promo discount exists
  const hasPromoDiscount =
    isPromoCodeApplied &&
    cover?.onlinePresentationTotalWithDiscount &&
    cover?.onlinePresentationTotalWithDiscount < (cover?.total ?? 0);

  const showOfflinePrice = (value: number) => {
    // Hide strikethrough price if flag is enabled
    if (isHideStrikethroughAndExclusivePrice) {
      return null;
    }

    return (
      <span
        className={`line-through text-2xs leading-[24px] font-normal ${headerDetaisClassNames?.subTextClassName} `}
      >
        {value
          ? `${
              (current.brandSpecificParams?.currencySymbol as string) ?? ""
            }${value.toFixed(2)}`
          : ""}
      </span>
    );
  };

  const renderExclusiveBanner = (
    isShow: boolean,
    bgColor: string,
    bannerLabel?: string,
    app?: string,
    showOfflineTotalPrice?: boolean,
    loyaltyDiscountEligible?: boolean
  ) => {
    // Hide exclusive banner if flag is enabled
    if (isHideStrikethroughAndExclusivePrice) {
      return null;
    }

    if (
      (app === Quote && showOfflineTotalPrice && isShow) ||
      (app === RenewalPage && isShow && loyaltyDiscountEligible)
    ) {
      return (
        <div
          className="absolute w-[100%]"
          style={{ transform: `translateY(-50%)` }}
        >
          <div className="z-10 flex justify-center ">
            <OnlineExclusiveBanner
              onlineExclusiveBannerLabel={bannerLabel ?? ""}
              background={bgColor}
              dataTestid={`${cover?.policyType}-${cover?.policyPackage}`}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  const showPrice = (value: number | undefined) => {
    return value
      ? `${
          (current.brandSpecificParams?.currencySymbol as string) ?? ""
        }${value.toFixed(2)}`
      : "";
  };

  const renderTotalPrice = (
    total: number,
    onlinePresentationTotalWithDiscount?: number,
    forceHorizontal?: boolean
  ) => {
    if (cover) {
      // For Avanti with monthly payment OR when forceHorizontal is true, use Staysure style
      if (
        PRODUCT === BRAND_AVANTI &&
        !forceHorizontal &&
        (cover?.collectionAmount === null ||
          cover?.collectionAmount === undefined)
      ) {
        return showPrice(onlinePresentationTotalWithDiscount ?? total);
      }
      // For Staysure OR Avanti with monthly payment, use getPriceComparison for horizontal layout with strikethrough
      return getPriceComparison(
        total,
        onlinePresentationTotalWithDiscount,
        showPrice
      );
    }

    return "";
  };

  const renderTotalPriceWithoutDiscounts = (
    offlineTotal?: number,
    app?: string,
    loyaltyDiscountEligible?: boolean,
    finalRetailENBPrice?: number,
    isShowOfflinePrice?: boolean
  ) => {
    if (isShowOfflinePrice && app === Quote) {
      return showOfflinePrice(offlineTotal ?? 0);
    }
    if (loyaltyDiscountEligible && app === RenewalPage) {
      return showOfflinePrice(finalRetailENBPrice || 0);
    }
    return null;
  };

  function handleOfflinePrice(
    showOfflinePriceFunction: (price: number) => React.JSX.Element | null,
    finalRetailENBPrice?: number,
    offlineTotal?: number,
    showOfflineTotalPrice?: boolean,
    loyaltyDiscountEligible?: boolean,
    app?: string
  ): string | React.JSX.Element | null {
    if (showOfflineTotalPrice && app === Quote) {
      return showOfflinePriceFunction(offlineTotal || 0);
    }

    if (loyaltyDiscountEligible && app === RenewalPage) {
      return showOfflinePriceFunction(finalRetailENBPrice || 0);
    }

    // Hide "in full or" text if flag is enabled
    if (isHideStrikethroughAndExclusivePrice) {
      return "";
    }

    return "in full or";
  }

  useEffect(() => {
    if (cardTopData.isLoading) {
      dispatch(updateisLoadingCoverState(true));
    } else {
      dispatch(updateisLoadingCoverState(false));
    }
  }, [cardTopData.isLoading, dispatch]);

  return (
    <div className="relative">
      {!isHideExclusiveBanner &&
        renderExclusiveBanner(
          isShowExclusiveBanner.isShow && !isPromoCodeApplied,
          isShowExclusiveBanner.bgColor,
          onlineExclusiveBannerLabel,
          application,
          cover?.showOfflinePrice,
          cover?.loyaltyDiscountEligible
        )}
      <div
        data-testid="CoveringCard-top-testidd"
        className={`${topSectionclassName} ${
          styles.backgroundColor
        } rounded-t-[16px] ${
          isSelect ? "pt-spacing-xl" : "pt-spacing-m"
        } pb-spacing-m pr-spacing-m w-full`}
      >
        {shouldShowWhichBadge && (
          <div
            className={`${styles.whichBadgeContainer} ${
              isPriceToggleEnabled ? styles.whichBadgeContainerPriceToggle : ""
            } ${isSelect ? styles.whichBadgeContainerSelected : ""}`}
          >
            {isRenewalBadge ? (
              <Image
                loader={() => renewalBadgeSrc}
                src={renewalBadgeSrc}
                width={94}
                height={75}
                alt="Which? Best Buy"
                objectFit="cover"
              />
            ) : (
              <ImageAtom
                image={whichBestBuyBadge as MagnoliaImage}
                altText="Which? Best Buy"
                width={94}
                height={75}
                styleClass={styles.whichBadge}
              />
            )}
          </div>
        )}
        <div
          className={`${headerClassName} px-spacing-m font-header-font md:text-[31px] text-[26px] font-[400] md:leading-[34.1px] leading-[29.9px]`}
        >
          <p>{cardTopData?.cardTitle ?? ""}</p>
        </div>
        {current.brandSpecificParams?.isDisplayDefaqtoStarts && (
          <div
            className={`mt-spacing-s px-spacing-xs py-spacing-xxs ${defaqtoStyles.background} ${styles.defaqtoBg} w-fit h-[27px] flex justify-end items-center`}
          >
            <Defaqto stars={defaqtoStarts} />
          </div>
        )}
        <div className="pl-spacing-m  ">
          {(isInitialAMT || !isPriceToggleEnabled) &&
            type === ANNUAL_MULTI_TRIP &&
            referenceId &&
            !(
              cover?.collectionAmount === null ||
              cover?.collectionAmount === undefined
            ) && (
              <div className="inline-table leading-[150%] min-h-[16px] items-center font-body-font font-[700] mt-spacing-xs !h-[16px]">
                <p
                  className={` float-left ${headerDetaisClassNames?.mainTextClassName} text-[18px] min-h-[16px] !h-[16px]`}
                >
                  {renderTotalPrice(
                    cover?.total,
                    cover?.onlinePresentationTotalWithDiscount
                  )}
                </p>
                <p
                  className={`${
                    cover?.showOfflinePrice
                      ? `float-left ${offlinePriceSubTextColor} text-[18px] min-h-[16px] !h-[16px]`
                      : `${headerDetaisClassNames?.subTextClassName}  text-[18px] min-h-[16px] leading-[24px] float-left !h-[16px]`
                  } `}
                >
                  &nbsp;
                  {handleOfflinePrice(
                    showOfflinePrice,
                    cover?.finalRetailENBPrice,
                    cover?.offlineTotal,
                    cover?.showOfflinePrice,
                    cover?.loyaltyDiscountEligible,
                    application
                  )}
                </p>
              </div>
            )}
          <div
            className={` flex ${
              isExpandSelectButton || isPriceToggleEnabled
                ? "flex-col"
                : "justify-between items-center"
            } ${
              type === ANNUAL_MULTI_TRIP &&
              cover?.collectionAmount !== null &&
              cover?.collectionAmount !== undefined
                ? "mt-spacing-xxs" +
                  (isExpandSelectButton || isPriceToggleEnabled
                    ? ""
                    : " min-h-[44px]")
                : `${isHideExclusiveBanner ? "mt-spacing-m" : "mt-spacing-s"}`
            } ${
              shouldShowWhichBadge && isPriceToggleEnabled
                ? "md:!mt-spacing-m"
                : ""
            }`}
          >
            <div
              className={`${
                isExpandSelectButton
                  ? "w-full"
                  : isPriceToggleEnabled
                  ? "w-full"
                  : "w-[calc(100%-123px)]"
              } font-body-font font-[700]`}
            >
              {!isInitialAMT &&
              isPriceToggleEnabled &&
              referenceId &&
              onTripTypeToggle ? (
                <PriceToggle
                  cardType={keyValue}
                  currentTripType={type ?? ""}
                  onToggle={onTripTypeToggle}
                  isDisabled={isQuoteDisabled}
                />
              ) : (
                <>
                  {type === ANNUAL_MULTI_TRIP &&
                  referenceId &&
                  !(
                    cover?.collectionAmount === null ||
                    cover?.collectionAmount === undefined
                  ) ? (
                    <>
                      <div className="flex leading-[120%] font-body-font min-h-[16px] items-center justify-start">
                        <p
                          className={`${headerDetaisClassNames?.mainTextClassName} text-[18px] leading-[27px]`}
                        >
                          {cover
                            ? `${
                                (current.brandSpecificParams
                                  ?.currencySymbol as string) ?? ""
                              }${cover.collectionAmount?.toFixed(2)}`
                            : ""}

                          <span
                            className={`${headerDetaisClassNames?.subTextClassName} leading-[120%] text-[18px]`}
                          >
                            &nbsp;a month
                          </span>
                        </p>
                      </div>
                      <p
                        className={`${headerDetaisClassNames?.subTextClassName} leading-[24px] text-[16px] font-body-font font-normal`}
                      >
                        (
                        {`${
                          (current.brandSpecificParams
                            ?.currencySymbol as string) ?? ""
                        }${cover.totalCost?.toFixed(2)}`}{" "}
                        total)
                      </p>
                    </>
                  ) : (
                    referenceId && (
                      <div
                        className={`${
                          PRODUCT === BRAND_AVANTI
                            ? "flex flex-col"
                            : "flex items-center h-full"
                        }`}
                      >
                        <p
                          className={` ${headerDetaisClassNames?.mainTextClassName} md:text-[25px] text-[23px] leading-[115%]`}
                          data-testid={`price-card-${cover?.policyType}-${cover?.policyPackage}`}
                        >
                          {cover &&
                            renderTotalPrice(
                              cover?.total,
                              cover?.onlinePresentationTotalWithDiscount
                            )}
                          {PRODUCT === BRAND_AVANTI ? "" : " "}
                          {!isHideTotalPriceWithoutDiscounts &&
                            PRODUCT !== BRAND_AVANTI &&
                            (isPromoBannerActive ? hasPromoDiscount : true) &&
                            renderTotalPriceWithoutDiscounts(
                              cover?.offlineTotal,
                              application,
                              cover?.loyaltyDiscountEligible,
                              cover?.finalRetailENBPrice,
                              cover?.showOfflinePrice
                            )}
                        </p>
                        {PRODUCT === BRAND_AVANTI &&
                          !isHideTotalPriceWithoutDiscounts &&
                          (isPromoBannerActive
                            ? hasPromoDiscount
                            : cover?.showOfflinePrice) && (
                            <p className="font-body-font text-[16px] font-[400] leading-[24px] text-[rgb(79,77,76)] line-through">
                              {showPrice(
                                isPromoCodeApplied &&
                                  cover?.onlinePresentationTotalWithDiscount
                                  ? cover?.total
                                  : cover?.offlineTotal
                              )}
                            </p>
                          )}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
            {isPromoCodeApplied && PRODUCT === BRAND_STAYSURE && (
              <div className="pr-spacing-m mt-spacing-xs">
                <AppliedPromo promoCode={appliedPromoCode.code} />
              </div>
            )}
            <div
              className={`flex items-center ${
                isExpandSelectButton || isPriceToggleEnabled
                  ? "mt-spacing-s justify-between "
                  : "h-auto"
              } ${
                shouldShowWhichBadge
                  ? "md:mt-[20px]"
                  : keyValue === BASIC &&
                    isBasicCoverButtonMarginEnabled &&
                    isDisplayWhichBestBuyBadge
                  ? "md:mt-[20px]"
                  : ""
              }`}
              data-testid="select-btn-wrapper-testid"
            >
              <SmallButtonWrapper
                id={`cover-level-selection-${id}-card-select-button`}
                data-testid={`cover-level-selection-${id}-card-select-button`}
                activeLabel={cardTopData?.selectBtnActiveLable ?? ""}
                inactiveLabel={cardTopData?.selectBtnInactiveLable ?? ""}
                hierarchy="primary"
                isRightIcon
                className={`${styles.selectBtn} ${
                  !selectState ? "blockClass" : ""
                } ${
                  isExpandSelectButton || isPriceToggleEnabled
                    ? "w-full"
                    : "min-w-[74px]"
                } max-h-[40px] !text-[18px] leading-[100%] font-[600] font-body-font p-spacing-s disabled:cursor-not-allowed`}
                onClick={(state) =>
                  onSelect &&
                  onSelect(keyValue as keyof typeof headerClassNames, state, id)
                }
                state={selectState}
                isLoading={cardTopData.isLoading}
                loadingIcon={<BtnLoadingSvg />}
                isClickableDisabled={
                  referenceId === "" || referenceId === undefined
                }
                isDisabled={isQuoteDisabled}
              />
            </div>
          </div>
          {isPromoCodeApplied && PRODUCT === BRAND_AVANTI && (
            <div className="pr-spacing-m mt-spacing-xs">
              <AppliedPromo promoCode={appliedPromoCode.code} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withApplication(CoverSingleCardTop);
